import Vue from "vue";

import { DictationTile } from "@joyz/app-core/models/control";

import {
  IsDictationProblemsHandler,
  DictationProblemsState,
  ApiDictationProblem,
  ChatDictationState,
  DictationEvent,
  ApiDictationQuestion
} from "@tt-app/models/exercise/dictation";
import { BaseHandler, exerciseHandler } from "../common";
import {
  ChatState,
  ExerciseState,
  ChatAllDisplayableObject,
  ApiChatDisplayableObject
} from "@tt-app/models/exercise";
import { api } from "@tt-app/api";
import {
  AppChatDictationUserSpeaker,
  AppChatDictationUserAnswer,
  AppChatDictationCorrection
} from "@tt-app/models/exercise/dictation/chat";
import { MainLogger } from "@tt-app/utils/internal/logger";

class DictationHandler extends BaseHandler implements IsDictationProblemsHandler {
  // --------------------------------------------------------------------------
  //  Initialisation
  // --------------------------------------------------------------------------
  constructor() {
    super();
  }

  // --------------------------------------------------------------------------
  //  Methods
  // --------------------------------------------------------------------------
  getNewState(): DictationProblemsState {
    return {
      current: undefined,
      question: undefined,
      results: [],
      retryCount: -1,
      inQueue: []
    };
  }

  loadNextProblem(state: DictationProblemsState, chatState: ChatState): ChatDictationState {
    const problem: ApiDictationProblem = chatState.display.lastItem as ApiDictationProblem;

    const dictation: DictationProblemsState = {
      ...state,
      // Load the new problem
      current: problem,
      // Reset error count
      retryCount: 0,
      // Add a new empty result
      results: problem
        ? [
            ...state.results,
            {
              uuid: problem.problemUuid,
              sentenceIndex: problem.sentenceIndex,
              retryCount: 0
            }
          ]
        : state.results
    };

    return this.loadNextProblemAttempt(dictation, chatState);
  }

  loadNextProblemAttempt(state: DictationProblemsState, chatState: ChatState): ChatDictationState {
    if (!state.current) {
      this._warn("loadNextProblemAttempt cannot load from an empty current problem");
      return { chat: chatState, dictation: state };
    }

    // Question to display
    let question: DictationTile[] | undefined = undefined;

    // Transform the current attempt messages
    const toQueueMessages: ChatAllDisplayableObject[] = state.current.attempts[
      state.retryCount
    ].components.map((component): ApiChatDisplayableObject | AppChatDictationUserSpeaker => {
      if (component.type === "DICTATION_QUESTION") {
        // save the question to the state
        question = component.question;

        // build the speaker icon message
        const speakerMsg: AppChatDictationUserSpeaker = {
          type: "CHAT_DICTATION_USER_SPEAKER",
          audioUrls: [component.audio],
          autoPlay: true,
          avatar: component.avatar,
          side: "right"
        };
        return speakerMsg;
      }

      return component;
    });

    return {
      chat: {
        ...chatState,
        messages: {
          ...chatState.messages,
          inQueue: [...chatState.messages.inQueue, ...toQueueMessages]
        },
        status: question ? "ProblemDisplaying" : "MsgBotPlaying"
      },
      dictation: {
        ...state,
        question
      }
    };
  }

  async onUserAnswerSent<V extends Vue>(
    component: V,
    state: DictationProblemsState,
    chatState: ChatState,
    exerciseState: ExerciseState,
    answer: DictationTile[],
    userActions: DictationEvent[]
  ): Promise<ChatDictationState> {
    if (!state.current) {
      this._warn("onUserAnswerSent cannot perform from an empty current problem");
      return { chat: chatState, dictation: state };
    }
    const currentProblem = state.current;
    // TODO: error handling?
    if (!currentProblem) {
      MainLogger.warn(`Dictation handler onUserAnswerSent: state.current problem is undefined!`);
      return { chat: chatState, dictation: state };
    }

    const currentQuestion = currentProblem.attempts[state.retryCount].components
      .filter((item): item is ApiDictationQuestion => item.type === "DICTATION_QUESTION")
      .find(() => true);

    // TODO: error handling?
    if (!currentQuestion) {
      MainLogger.warn(`Dictation handler onUserAnswerSent: current question is undefined!`);
      return { chat: chatState, dictation: state };
    }

    // Submit answer
    const authInfo = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const dictationResp = await api.exercise.submitDictationAnswer({
      ...authInfo,
      logId: exerciseState.logId,
      // API does not allow extra fields such as `selectedIndex`
      answer: answer.map((a): DictationTile => ({ text: a.text, fixed: a.fixed })),
      problemUuid: currentProblem.problemUuid,
      question: currentQuestion.question,
      retry: state.retryCount,
      sentenceIndex: currentProblem.sentenceIndex,
      userActions: userActions
    });

    // Remove speaker message and add answer
    const curatedDisplay: ChatAllDisplayableObject[] = chatState.messages.toDisplay.filter(
      item => item.type !== "CHAT_DICTATION_USER_SPEAKER"
    );

    const userAnswer: AppChatDictationUserAnswer = {
      type: "CHAT_DICTATION_USER_ANSWER",
      avatar: currentQuestion.avatar,
      correctedAnswer: dictationResp.correction || [],
      side: "right",
      isCorrect: dictationResp.isCorrect
    };
    let userAnswers: ChatAllDisplayableObject[] = [userAnswer];

    if (dictationResp.sentence) {
      // When the user answer is correct, the response is returned in `sentence` so `correction`
      // is empty
      if (dictationResp.isCorrect) {
        userAnswer.correctedAnswer = [{ text: dictationResp.sentence, isCorrect: true }];
      }
      // User has reached the maximum number of retries and the correction will be displayed
      else {
        const currentAudio: string = currentQuestion.audio;

        const correction: AppChatDictationCorrection = {
          type: "CHAT_DICTATION_CORRECTION",
          sentence: dictationResp.sentence || "",
          audioUrls: [currentAudio],
          autoPlay: false
        };
        userAnswers = [...userAnswers, correction];
      }
    }

    const newChat: ChatState = {
      ...chatState,
      messages: {
        toDisplay: curatedDisplay,
        inQueue: userAnswers
      }
    };

    // remove questions
    state.question = undefined;

    // Update dictation state
    if (!dictationResp.isCorrect) {
      state.retryCount++;
      state.results
        .filter(
          result =>
            result.uuid === currentProblem.problemUuid &&
            result.sentenceIndex === currentProblem.sentenceIndex
        )
        .forEach(result => result.retryCount++);

      if (state.retryCount < currentProblem.attempts.length) {
        return this.loadNextProblemAttempt(state, newChat);
      }

      this._warn(`Invalid retryCount ${state.retryCount} for problem ${currentProblem}`);
    }

    // It's correct \o/
    newChat.status = "MsgBotPlaying";

    return {
      chat: newChat,
      dictation: state
    };
  }
}

export const dictationProblemsHandler = new DictationHandler();
